import * as THREE from 'three'
import {
  collectAssemblyNodeIdentityLookupIds,
  splitAssemblyNodePath,
} from '../../assembly/utils/AssemblyNodePath'
import { parseImportedBodyEntries } from '../../imported-model/loaders/body-entries'
import type { GlbHierarchyNode } from '../../imported-model/loaders/glb'
import { ImportedModelObjectMetadataService } from '../../imported-model/metadata'
import { resolveImportedBodyOccurrenceNodeId } from '../../imported-model/utils'
import type { ModelAssetFile, ModelAssetNode } from '../assets/model-asset-types'

const PROJECT_MODEL_REPRESENTED_PACKAGE_RESOURCE_FLAG = 'isProjectModelRepresentedPackageResource'

/**
 * 判断未知值是否为普通记录对象。
 *
 * @param value 待判断的值。
 * @returns 非数组对象返回 true。
 */
function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

/**
 * 判断项目模型节点包是否为 `.puppy` 离线包。
 *
 * @param file 项目模型节点包文件。
 * @returns 是否为 PuppyCAD 离线包。
 */
function isProjectModelPuppyPackageFile(file: ModelAssetFile): boolean {
  if (file.loadMode === 'complete-glb-scene') {
    return false
  }
  const normalizedKind = file.kind.trim().toLowerCase()
  const normalizedFileId = file.fileId.trim().toLowerCase()

  return normalizedKind === '.puppy' || normalizedFileId.endsWith('.puppy')
}

/**
 * 向身份集合中追加按装配节点规则归一化后的 lookup ID。
 *
 * @param target 待写入的身份集合。
 * @param value 原始身份字段。
 */
function appendAssemblyNodeIdentityLookupIds(target: Set<string>, value: unknown): void {
  collectAssemblyNodeIdentityLookupIds(value).forEach((lookupId) => {
    target.add(lookupId)
  })
}

/**
 * 向身份集合中追加装配节点路径后缀。
 *
 * 父工作区中的显式子节点可能是 `父路径/包内根`，而 `.puppy` 镜像对象只携带
 * `包内根/零件`。去重时需要纳入不含父路径的后缀身份，但不能追加单独根段，
 * 否则会把整个项目根误判为可匹配前缀。
 *
 * @param target 待写入的身份集合。
 * @param value 原始装配节点身份。
 */
function appendAssemblyNodeSuffixLookupIds(target: Set<string>, value: unknown): void {
  collectAssemblyNodeIdentityLookupIds(value).forEach((lookupId) => {
    const segments = splitAssemblyNodePath(lookupId)
    for (let index = 1; index < segments.length; index += 1) {
      appendAssemblyNodeIdentityLookupIds(target, segments.slice(index).join('/'))
    }
  })
}

/**
 * 创建项目模型显式子节点的稳定身份集合。
 *
 * root `.puppy` 补树时，显式 `ModelAssetNode.children` 表达已被项目模型物化的 occurrence；
 * 它可能没有与包内 hierarchy 完全一致的 nodeKey，因此必须同时纳入各层显式身份。
 *
 * @param node 项目模型中的显式子节点。
 * @returns 可用于和 `.puppy` 包内 hierarchy 对比的身份集合。
 */
function createProjectChildIdentityLookupSet(node: ModelAssetNode): ReadonlySet<string> {
  const lookupIds = new Set<string>()

  appendAssemblyNodeIdentityLookupIds(lookupIds, node.nodeKey)
  appendAssemblyNodeSuffixLookupIds(lookupIds, node.nodeKey)
  appendAssemblyNodeIdentityLookupIds(lookupIds, node.displayName)
  appendAssemblyNodeIdentityLookupIds(lookupIds, node.instanceUuid)
  appendAssemblyNodeIdentityLookupIds(lookupIds, node.persistedNodeId)
  if (node.assetSource?.kind === 'embedded-puppy-occurrence') {
    appendAssemblyNodeIdentityLookupIds(lookupIds, node.assetSource.assemblyNodeId)
  }

  return lookupIds
}

/**
 * 创建 `.puppy` 包内 hierarchy 节点的稳定身份集合。
 *
 * @param node `.puppy` 包内折叠后的 hierarchy 节点。
 * @returns 可用于和项目显式子节点对比的身份集合。
 */
function createPackageHierarchyIdentityLookupSet(node: GlbHierarchyNode): ReadonlySet<string> {
  const lookupIds = new Set<string>()

  appendAssemblyNodeIdentityLookupIds(lookupIds, node.name)
  appendAssemblyNodeIdentityLookupIds(lookupIds, node.path)
  appendAssemblyNodeIdentityLookupIds(lookupIds, node.path.split('/').filter(Boolean).at(-1))
  appendAssemblyNodeIdentityLookupIds(lookupIds, node.meta?.assemblyNodeId)
  appendAssemblyNodeIdentityLookupIds(lookupIds, node.meta?.externalAssemblyName)

  return lookupIds
}

/**
 * 从 bodyBindingId 的 occurrence 作用域中补充装配节点身份。
 *
 * 历史 `.puppy` 的可见 mesh 可能把 assemblyNodeId 写成包根，但 bodyBindingId 仍保留
 * 真实 occurrence。这里只解析显式 occurrence，不把纯 body 定义身份用于去重。
 *
 * @param target 待写入的身份集合。
 * @param value bodyBindingId 候选值。
 */
function appendBodyBindingOccurrenceLookupIds(target: Set<string>, value: unknown): void {
  const occurrenceNodeId = typeof value === 'string'
    ? resolveImportedBodyOccurrenceNodeId(value)
    : ''
  appendAssemblyNodeIdentityLookupIds(target, occurrenceNodeId)
}

/**
 * 从 body entry 列表中补充 occurrence 身份。
 *
 * @param target 待写入的身份集合。
 * @param entries 原始 body entries。
 */
function appendBodyEntryOccurrenceLookupIds(target: Set<string>, entries: unknown): void {
  parseImportedBodyEntries(entries).forEach((entry) => {
    appendBodyBindingOccurrenceLookupIds(target, entry.bodyBindingId)
  })
}

/**
 * 从 primitive entry 列表中补充 occurrence 身份。
 *
 * @param target 待写入的身份集合。
 * @param entries 原始 primitive entries。
 */
function appendPrimitiveEntryOccurrenceLookupIds(target: Set<string>, entries: unknown): void {
  if (!Array.isArray(entries)) {
    return
  }
  entries.forEach((entry) => {
    if (!isRecord(entry)) {
      return
    }
    appendBodyBindingOccurrenceLookupIds(target, entry.bodyBindingId)
  })
}

/**
 * 判断包内身份是否落在显式项目子节点表示的 occurrence 下。
 *
 * @param packageLookupId 包内 hierarchy 或 Object3D 的身份 lookup。
 * @param projectLookupId 显式项目子节点的身份 lookup。
 * @returns 包内身份等于或位于项目子节点身份之下时返回 true。
 */
function isAssemblyIdentityInsideRepresentedProjectChild(
  packageLookupId: string,
  projectLookupId: string,
): boolean {
  if (packageLookupId === projectLookupId || packageLookupId.startsWith(`${projectLookupId}/`)) {
    return true
  }

  const packageSegments = splitAssemblyNodePath(packageLookupId)
  const projectSegments = splitAssemblyNodePath(projectLookupId)
  if (projectSegments.length === 0 || packageSegments.length < projectSegments.length) {
    return false
  }

  for (let startIndex = 0; startIndex <= packageSegments.length - projectSegments.length; startIndex += 1) {
    const isMatched = projectSegments.every((segment, segmentIndex) => (
      packageSegments[startIndex + segmentIndex] === segment
    ))
    if (isMatched) {
      return true
    }
  }

  return false
}

/**
 * 判断一组包内身份是否被任一项目子节点接管。
 *
 * @param packageLookupIds 包内对象或 hierarchy 的身份集合。
 * @param projectChildIdentitySets 显式项目子节点身份集合列表。
 * @returns 命中任一显式项目子节点时返回 true。
 */
function isPackageIdentityRepresentedByProjectChild(
  packageLookupIds: ReadonlySet<string>,
  projectChildIdentitySets: readonly ReadonlySet<string>[],
): boolean {
  for (const projectLookupIds of projectChildIdentitySets) {
    for (const packageLookupId of packageLookupIds) {
      for (const projectLookupId of projectLookupIds) {
        if (isAssemblyIdentityInsideRepresentedProjectChild(packageLookupId, projectLookupId)) {
          return true
        }
      }
    }
  }

  return false
}

/**
 * 判断包内 hierarchy 节点是否已经由显式项目子节点表达。
 *
 * @param packageChild `.puppy` 包内折叠后的直接子层级。
 * @param projectChildIdentitySets 显式项目子节点身份集合列表。
 * @returns 命中任意显式子节点时返回 true。
 */
function isPackageHierarchyChildRepresentedByProjectChild(
  packageChild: GlbHierarchyNode,
  projectChildIdentitySets: readonly ReadonlySet<string>[],
): boolean {
  return isPackageIdentityRepresentedByProjectChild(
    createPackageHierarchyIdentityLookupSet(packageChild),
    projectChildIdentitySets,
  )
}

/**
 * 解析 `.puppy` 包可用于去重的项目子节点身份集合。
 *
 * @param input 当前项目节点、包文件与可选子节点过滤器。
 * @returns 可以参与包内镜像去重的子节点身份集合；不适用时返回空数组。
 */
export function createProjectChildIdentityLookupSetsForPackage(input: {
  readonly node: ModelAssetNode
  readonly packageFile: ModelAssetFile
  readonly childFilter?: (child: ModelAssetNode) => boolean
}): readonly ReadonlySet<string>[] {
  if (
    input.node.nodeType !== 'assembly'
    || input.node.children.length === 0
    || !isProjectModelPuppyPackageFile(input.packageFile)
  ) {
    return []
  }

  return input.node.children
    .filter(input.childFilter ?? (() => true))
    .map(createProjectChildIdentityLookupSet)
}

/**
 * 过滤已经被显式项目子节点物化的 `.puppy` 包内直接子层级。
 *
 * 初始加载的 root `.puppy` 可能只补入对比新增节点，而包内仍承载其它已有节点；
 * 因此只能逐个 occurrence 对比后去重，不能因存在项目子节点就隐藏整个包内 hierarchy。
 *
 * @param input 包内折叠子层级与显式项目子节点身份集合。
 * @returns 应继续投影到设计树中的包内直接子层级。
 */
export function filterPackageHierarchyChildrenRepresentedByProjectChildren(input: {
  readonly foldedPackageChildren: readonly GlbHierarchyNode[]
  readonly projectChildIdentitySets: readonly ReadonlySet<string>[]
}): readonly GlbHierarchyNode[] {
  if (input.projectChildIdentitySets.length === 0) {
    return input.foldedPackageChildren
  }

  return input.foldedPackageChildren.filter((packageChild) => (
    !isPackageHierarchyChildRepresentedByProjectChild(packageChild, input.projectChildIdentitySets)
  ))
}

/**
 * 将已经被项目显式子节点接管的包内对象标记为仅保留资源。
 *
 * @param object 需要从组件交互链路中剔除的包内对象根。
 */
function markProjectRepresentedPackageObjectResource(object: THREE.Object3D): void {
  object.visible = false
  object.userData = {
    ...object.userData,
    skipSelection: true,
    skipSelectionSubtree: true,
    [PROJECT_MODEL_REPRESENTED_PACKAGE_RESOURCE_FLAG]: true,
  }
}

/**
 * 按 hierarchy 节点把对应场景子树标记为资源保留子树。
 *
 * @param node 被项目显式子节点替代的包内 hierarchy 节点。
 * @param objectByUuid 当前包对象内的 Object3D 索引。
 */
function markProjectRepresentedPackageHierarchySceneResource(
  node: GlbHierarchyNode,
  objectByUuid: ReadonlyMap<string, THREE.Object3D>,
): void {
  const object = objectByUuid.get(node.objectId)
  if (object) {
    markProjectRepresentedPackageObjectResource(object)
    return
  }

  node.children.forEach((child) => {
    markProjectRepresentedPackageHierarchySceneResource(child, objectByUuid)
  })
}

/**
 * 读取 Object3D 自身携带的导入装配身份集合。
 *
 * @param object 当前包内场景对象。
 * @returns 可用于与项目显式子节点比对的身份集合。
 */
function createPackageObjectIdentityLookupSet(object: THREE.Object3D): ReadonlySet<string> {
  const lookupIds = new Set<string>()
  const metadata = ImportedModelObjectMetadataService.peekOwnMetadata(object)
  const glbHierarchy = isRecord(object.userData?.glbHierarchy)
    ? object.userData.glbHierarchy
    : undefined
  const glbHierarchyMeta = isRecord(glbHierarchy?.meta)
    ? glbHierarchy.meta
    : undefined

  appendAssemblyNodeIdentityLookupIds(lookupIds, metadata?.assemblyNodeId)
  appendAssemblyNodeIdentityLookupIds(lookupIds, metadata?.selectionTarget?.assemblyNodeId)
  appendAssemblyNodeIdentityLookupIds(lookupIds, metadata?.hierarchy?.meta?.assemblyNodeId)
  appendAssemblyNodeIdentityLookupIds(lookupIds, glbHierarchyMeta?.assemblyNodeId)
  appendAssemblyNodeIdentityLookupIds(lookupIds, object.userData?.puppy?.assemblyNodeId)
  appendAssemblyNodeIdentityLookupIds(lookupIds, object.userData?.assemblyNodeId)
  appendBodyEntryOccurrenceLookupIds(lookupIds, metadata?.hierarchy?.meta?.bodyEntries)
  appendBodyEntryOccurrenceLookupIds(lookupIds, object.userData?.importedPartBodyEntries)
  appendPrimitiveEntryOccurrenceLookupIds(lookupIds, object.userData?.primitiveEntityEntries)
  appendBodyBindingOccurrenceLookupIds(lookupIds, object.userData?.importedBodyBindingId)

  return lookupIds
}

/**
 * 扫描包内场景对象，把已被显式项目子节点接管的镜像对象降级为资源对象。
 *
 * @param input 包根对象与显式项目子节点身份集合。
 */
function markPackageObjectsRepresentedByProjectChildren(input: {
  readonly packageObject: THREE.Object3D
  readonly projectChildIdentitySets: readonly ReadonlySet<string>[]
}): void {
  if (input.projectChildIdentitySets.length === 0) {
    return
  }

  input.packageObject.traverse((object) => {
    if (object === input.packageObject) {
      return
    }

    if (isPackageIdentityRepresentedByProjectChild(
      createPackageObjectIdentityLookupSet(object),
      input.projectChildIdentitySets,
    )) {
      markProjectRepresentedPackageObjectResource(object)
    }
  })
}

/**
 * 同步隔离已经从设计树中过滤掉的包内场景子树。
 *
 * root `.puppy` 重新加载时，包内原始 occurrence 和 OpenProject 显式子节点可能同时存在。
 * 设计树过滤只能解决显示重复；若场景对象仍参与组件索引，拖动和 Mate 会继续命中同源重复实例。
 * 因此组合层必须把这些包内子树降级为资源保留对象，随根包释放但不再参与交互语义。
 *
 * @param input 过滤前后的包内 hierarchy 子节点与场景对象索引。
 */
export function markPackageHierarchyChildrenRepresentedByProjectChildren(input: {
  readonly foldedPackageChildren: readonly GlbHierarchyNode[]
  readonly retainedPackageChildren: readonly GlbHierarchyNode[]
  readonly objectByUuid: ReadonlyMap<string, THREE.Object3D>
  readonly projectChildIdentitySets: readonly ReadonlySet<string>[]
  readonly packageObject: THREE.Object3D
}): void {
  if (input.projectChildIdentitySets.length === 0) {
    return
  }

  const retainedObjectIds = new Set(input.retainedPackageChildren.map((child) => child.objectId))
  input.foldedPackageChildren.forEach((packageChild) => {
    if (retainedObjectIds.has(packageChild.objectId)) {
      return
    }
    markProjectRepresentedPackageHierarchySceneResource(packageChild, input.objectByUuid)
  })
  markPackageObjectsRepresentedByProjectChildren({
    packageObject: input.packageObject,
    projectChildIdentitySets: input.projectChildIdentitySets,
  })
}
